import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, LogOut, User, ChevronDown, LogIn, UserPlus } from "lucide-react";
import { useState } from "react";
import { isAuthenticated, clearToken } from "@/lib/auth";
import { useCompanyInfo } from "@/lib/company";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export const Navbar = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const legacyAuthed = isAuthenticated(); // fallback for pre-context bootstrapping
  const company = useCompanyInfo();
  const v = company?.logo_version || 1;
  const logoSrc = company?.logo_horizontal_url || "/branding/solarsim-logo-horizontal.png";
  const brandName = company?.brand_name || "SolarSim";
  const [menuOpen, setMenuOpen] = useState(false);

  // If the user is admin-only, keep legacy admin logout at top-right.
  const isAdmin = user?.role === "admin";
  const isCustomer = user?.role === "customer";

  const handleAdminLogout = () => {
    clearToken();
    logout();
    navigate("/admin/login");
  };

  const linkCls = ({ isActive }) =>
    `text-sm font-medium tracking-tight transition-colors ${
      isActive ? "text-solar-green" : "text-slate-600 hover:text-solar-slate"
    }`;

  return (
    <header data-testid="navbar" className="sticky top-0 z-40 w-full border-b border-slate-200/70 bg-white/80 backdrop-blur">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2 group">
          <img src={`${logoSrc}?v=${v}`} alt={brandName} loading="eager" className="h-20 w-auto md:h-24 object-contain transition-transform group-hover:scale-105" />
        </Link>

        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <NavLink to="/catalogue" data-testid="nav-catalogue" className={linkCls}>{t("nav.catalogue")}</NavLink>
          <NavLink to="/subventions" data-testid="nav-subsidies" className={linkCls}>{t("nav.subsidies")}</NavLink>
          <NavLink to="/simulateur" data-testid="nav-simulator" className={linkCls}>{t("nav.simulator")}</NavLink>

          {/* Admin quick link (visible for admins only when logged in) */}
          {isAdmin && (
            <NavLink to="/admin" data-testid="nav-admin" className={linkCls}>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />{t("nav.admin")}</span>
            </NavLink>
          )}

          {/* Customer user menu */}
          {isCustomer && (
            <div className="relative">
              <button type="button" onClick={() => setMenuOpen((x) => !x)} data-testid="user-menu-btn"
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-solar-slate hover:border-solar-green">
                <User className="h-3.5 w-3.5" />
                <span className="max-w-[100px] truncate">{user.first_name || user.email}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 rounded-xl border border-slate-200 bg-white p-2 shadow-lg" data-testid="user-menu">
                  <Link to="/account" onClick={() => setMenuOpen(false)} data-testid="user-menu-account"
                        className="block rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100">{t("nav.account")}</Link>
                  <Link to="/account/quotes" onClick={() => setMenuOpen(false)} data-testid="user-menu-quotes"
                        className="block rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100">{t("nav.my_quotes")}</Link>
                  <button type="button" onClick={() => { setMenuOpen(false); logout(); navigate("/"); }}
                          data-testid="user-menu-logout"
                          className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-100">
                    <LogOut className="h-3.5 w-3.5" /> {t("nav.logout")}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Anonymous CTAs — visibles dès que le contexte n'a pas de user, même si un token legacy traîne */}
          {!user && (
            <>
              <Link to="/login" data-testid="nav-login" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-solar-green">
                <LogIn className="h-4 w-4" /> {t("nav.login")}
              </Link>
              <Link to="/signup" data-testid="nav-signup" className="inline-flex items-center gap-1.5 rounded-full bg-solar-green px-3 py-1.5 text-xs font-semibold text-white hover:bg-solar-green-dark">
                <UserPlus className="h-3.5 w-3.5" /> {t("nav.signup")}
              </Link>
            </>
          )}

          {/* Legacy admin logout (context user unknown → fallback) */}
          {legacyAuthed && !user && (
            <button type="button" onClick={handleAdminLogout} data-testid="admin-logout-btn"
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-solar-green hover:text-solar-green">
              <LogOut className="h-3.5 w-3.5" /> {t("nav.logout")}
            </button>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
